import { Avatar, Badge, Button, Card, CardFooter, CardHeader, Drawer, EmptyState, MetricStrip, MetricTile, Skeleton, Table, ViewAll, formatDate, formatMoney, formatMoneyCompact, formatTime, AttentionRow, type Column } from "@helmr/ui";
import { useMemo, useState, type ReactElement } from "react";
import { Link } from "react-router-dom";
import type { DueRow } from "../../api/dues";
import type { BillSummary, Occurrence } from "../../api/lists";
import { addDays, hhmmToMinutes, minutesOfDay } from "../../lib/dates";
import { useDues, useFollowups, useOccurrences, useRangeData, type Range, type Scope } from "./hooks";
import { byMethod, countBills, countVisits, deltaTone, hourlyCollections, niceStep, owes, percentChange, sharePct, sumKnown, sumMinor, summariseDues, totalCollected, upToMinute } from "./metrics";
import { FailureBody, NotConnectedBody } from "./states";
import { home } from "./strings";
import s from "./home.module.css";

const t = home;

/** Without `/me` and without a row to read it from there is no currency: show a dash, never bare paise and never a crash. */
// A figure the server withheld (a D57 field lever is off) is null and shows as a dash, never as zero.
const moneyIn = (currency: string, locale: string) => (v: number | null) => (currency && v !== null ? formatMoney(v, currency, locale) : "—");

const CardSkeleton = ({ rows = 4 }: { rows?: number }) => (
  <div aria-busy="true">{Array.from({ length: rows }, (_, i) => <div className={s.skRow} key={i}><Skeleton width={32} height={32} /><div><Skeleton width="55%" height={13} /><Skeleton width="75%" height={12} /></div></div>)}</div>
);

/* ------------------------------------------------------------------ metrics strip */

export function MetricsCard({ sc, range }: { sc: Scope; range: Range }) {
  const d = useRangeData(sc, range);
  const dues = useDues(sc);
  const { locale, me } = sc.ctx;
  const currency = me?.business.currency ?? d.paymentsCur.data?.currency ?? dues.data?.currency ?? d.billsCur.data?.bills[0]?.currency ?? "";
  const nowMin = minutesOfDay(sc.nowMs, sc.ctx.timeZone);
  /** Today is compared with last week up to this minute; a finished day with the whole of last week's. */
  const soFar = <T,>(rows: readonly T[], at: (row: T) => number) => (range === "today" ? upToMinute(rows, at, sc.ctx.timeZone, nowMin) : rows);
  const tiles: Array<{ id: string; node: ReactElement }> = [];
  const pending = (d.canPay && d.paymentsCur.isPending) || (d.canBills && d.billsCur.isPending) || (d.canVisits && d.checkinsCur.isPending) || dues.isPending;
  const failure = [d.paymentsCur, d.paymentsPrev, d.billsCur, d.billsPrev, d.checkinsCur, d.checkinsPrev].find((q) => q.error)?.error ?? dues.error;
  const money = moneyIn(currency, locale);
  const hours = d.paymentsCur.data ? hourlyCollections(d.paymentsCur.data.collections, sc.ctx.timeZone) : null;

  if (d.canPay && d.paymentsCur.data && d.paymentsPrev.data) {
    const cur = totalCollected(d.paymentsCur.data.collections);
    const prev = totalCollected(soFar(d.paymentsPrev.data.collections, (p) => p.createdAt));
    const pct = percentChange(cur, prev);
    tiles.push({
      id: "collections",
      node: <MetricTile key="c" label={t.metrics.collections} value={`${money(cur)}${d.paymentsCur.data.capped ? "+" : ""}`} note={t.metrics.vs(money(prev), t.metrics.lastWeek)} delta={pct === null ? { tone: "flat", children: "New" } : { tone: deltaTone(pct), direction: pct > 0 ? "up" : pct < 0 ? "down" : undefined, children: `${Math.abs(pct)}%` }} spark={hours && hours.values.length > 1 ? hours.values : undefined} sparkTone={pct === null || pct === 0 ? "neutral" : deltaTone(pct) === "good" ? "good" : "bad"} />,
    });
  }
  if (d.canBills && d.billsCur.data && d.billsPrev.data) {
    const cur = countBills(d.billsCur.data.bills);
    const prev = countBills(soFar(d.billsPrev.data.bills, (b) => b.createdAt));
    const pct = percentChange(cur, prev);
    tiles.push({ id: "bills", node: <MetricTile key="b" label={t.metrics.bills} value={d.billsCur.data.nextCursor ? `${cur}+` : cur} note={t.metrics.vsCount(prev, t.metrics.lastWeek)} delta={pct === null ? { tone: "flat", children: "New" } : { tone: deltaTone(pct), direction: pct > 0 ? "up" : pct < 0 ? "down" : undefined, children: `${Math.abs(pct)}%` }} /> });
  }
  if (d.canVisits && d.checkinsCur.data && d.checkinsPrev.data) {
    const cur = countVisits(d.checkinsCur.data.checkins);
    const prev = countVisits(soFar(d.checkinsPrev.data.checkins, (c) => c.scannedAt));
    const capped = d.checkinsCur.data.nextCursor !== null;
    const pct = percentChange(cur, prev);
    tiles.push({ id: "checkins", node: <MetricTile key="k" label={t.metrics.checkins} value={capped ? `${cur}+` : cur} note={t.metrics.vsCount(prev, t.metrics.lastWeek)} delta={pct === null ? { tone: "flat", children: "New" } : { tone: deltaTone(pct), direction: pct > 0 ? "up" : pct < 0 ? "down" : undefined, children: `${Math.abs(pct)}%` }} /> });
  }
  if (dues.enabled && dues.data) {
    // The server's totals cover every bill owing, not just the page, so the tile is exact (no "+").
    const { count, balance } = dues.data.totals;
    tiles.push({ id: "dues", node: <MetricTile key="d" label={t.metrics.dues} value={money(balance)} note={t.metrics.owe(count, false)} /> });
  }

  const none = !d.canPay && !d.canBills && !d.canVisits && !dues.enabled;
  if (none) return null;

  const when = range === "today" ? `${formatDate(d.cur, locale, "long")}, as of ${formatTime(sc.nowMs, sc.ctx.timeZone)}` : formatDate(d.cur, locale, "long");
  const cap = range === "today" ? t.metrics.cap(formatDate(d.cur, locale, "long"), formatTime(sc.nowMs, sc.ctx.timeZone), formatDate(d.prev, locale, "long")) : t.metrics.capDay(when, formatDate(d.prev, locale, "long"));

  return (
    <>
      <p className={s.cap}>{cap}</p>
      {pending ? (
        <Card aria-busy="true" aria-label={t.metrics.label}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)" }}>{[0, 1, 2, 3].map((i) => <div key={i} style={{ padding: "14px 16px", display: "grid", gap: 6 }}><Skeleton width={90} height={12} /><Skeleton width="min(120px,80%)" height={22} /><Skeleton width="70%" height={12} /></div>)}</div>
        </Card>
      ) : failure ? (
        <Card aria-label={t.metrics.label}><FailureBody error={failure} title={t.metrics.error} what="The day's numbers" onRetry={() => { for (const q of [d.paymentsCur, d.paymentsPrev, d.billsCur, d.billsPrev, d.checkinsCur, d.checkinsPrev]) if (q.error) void q.refetch(); if (dues.error) dues.refetch(); }} /></Card>
      ) : (
        <Card>
          <MetricStrip label={t.metrics.label}>{tiles.map((x) => x.node)}</MetricStrip>
        </Card>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ collections chart */

export function CollectionsCard({ sc, range }: { sc: Scope; range: Range }) {
  const d = useRangeData(sc, range);
  const { locale, timeZone, me } = sc.ctx;
  if (!d.canPay) return null;
  const q = d.paymentsCur;
  const currency = me?.business.currency ?? q.data?.currency ?? "";
  const money = moneyIn(currency, locale);
  const compact = (v: number) => (currency ? formatMoneyCompact(v, currency, locale) : "");

  const body = () => {
    if (q.isPending || d.paymentsPrev.isPending) return <div style={{ padding: "0 16px 16px" }} aria-busy="true"><Skeleton height={240} /></div>;
    const err = q.error ?? d.paymentsPrev.error;
    if (err) return <FailureBody error={err} title={t.collections.error} what="Collections" onRetry={() => { void q.refetch(); void d.paymentsPrev.refetch(); }} />;
    const cur = hourlyCollections(q.data!.collections, timeZone);
    const prev = hourlyCollections(d.paymentsPrev.data!.collections, timeZone);
    const lo = Math.min(cur.hours[0]!, prev.hours[0]!);
    const hi = Math.max(cur.hours.at(-1)!, prev.hours.at(-1)!);
    const hoursAll = Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
    const at = (src: typeof cur, h: number) => src.values[src.hours.indexOf(h)] ?? 0;
    const curV = hoursAll.map((h) => at(cur, h));
    const prevV = hoursAll.map((h) => at(prev, h));
    const total = sumMinor(curV);
    // The bars keep all of last week's day; the headline figures compare like with like.
    const prevHours = hourlyCollections(range === "today" ? upToMinute(d.paymentsPrev.data!.collections, (p) => p.createdAt, timeZone, minutesOfDay(sc.nowMs, timeZone)) : d.paymentsPrev.data!.collections, timeZone).values;
    const prevTotal = sumMinor(prevHours);
    const prevSlots = prevHours.filter((v) => v > 0).length;
    const pct = percentChange(total, prevTotal);
    const step = niceStep(Math.max(...curV, ...prevV, 1));
    const top = step * 4;
    const ticks = [0, 1, 2, 3, 4].map((i) => i * step);
    const modes = byMethod(q.data!.collections);
    const modeTotal = sumMinor(modes.map((m) => m.amount));
    const best = curV.reduce((bi, v, i) => (v > curV[bi]! ? i : bi), 0);
    const hourLabel = (h: number) => `${String(h).padStart(2, "0")}:00`;
    const dense = hoursAll.length > 10;
    const nonEmpty = curV.filter((v) => v > 0).length;
    const capped = q.data!.capped;
    return (
      <div className={s.cwrap}>
        <div className={s.chart}>
          <div className={s.ctop}>
            <b>{money(total)}{capped ? "+" : ""}</b>
            {pct !== null ? <span className={`${s.d} ${pct >= 0 ? s.up : s.dn}`} style={{ font: "inherit", marginLeft: 6 }}>{pct > 0 ? "↑ " : pct < 0 ? "↓ " : ""}{Math.abs(pct)}%</span> : null}
            <p>{range === "today" ? t.collections.headToday : t.collections.headYesterday} · {t.collections.vsEarlier(money(prevTotal))}</p>
          </div>
          <div className={s.plot}>
            <div className={s.yax}>{ticks.map((v) => <span key={v} style={{ top: `${100 - (v / top) * 100}%` }}>{compact(v)}</span>)}</div>
            <div className={s.grid}>
              {ticks.slice(1).map((v) => <div key={v} className={s.gl} style={{ top: `${100 - (v / top) * 100}%` }} />)}
              <div className={s.bars} style={{ gridTemplateColumns: `repeat(${hoursAll.length}, 1fr)` }}>
                {hoursAll.map((h, i) => (
                  <div key={h} className={s.day} tabIndex={0} aria-label={`${hourLabel(h)}: ${money(curV[i]!)}, ${t.collections.tipPrev.toLowerCase()} ${money(prevV[i]!)}`}>
                    <i style={{ height: `${(curV[i]! / top) * 100}%` }} />
                    <i className={s.p} style={{ height: `${(prevV[i]! / top) * 100}%` }} />
                    <span className={s.tip}><b>{hourLabel(h)}</b><br />{money(curV[i]!)}<br /><span>{t.collections.tipPrev}: {money(prevV[i]!)}</span></span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className={s.xax}><span /><div style={{ gridTemplateColumns: `repeat(${hoursAll.length}, 1fr)` }}>{hoursAll.map((h, i) => <span key={h}>{dense && i % 2 ? "" : String(h).padStart(2, "0")}</span>)}</div></div>
        </div>
        <div className={s.split}>
          <h3>{t.collections.mode}</h3>
          {modes.length === 0 ? <p style={{ color: "var(--color-text-secondary)" }}>{t.collections.noPayments}</p> : modes.map((m) => (
            <div className={s.mode} key={m.method}>
              <p><span>{t.collections.modes[m.method] ?? m.method}</span><span>{money(m.amount)}</span></p>
              <div className={s.bar}><i style={{ width: `${sharePct(m.amount, modeTotal)}%` }} /></div>
            </div>
          ))}
          <div className={s.avg}>
            <small>{t.collections.average(t.collections.hour)}</small>
            <b>{money(nonEmpty ? Math.trunc(total / nonEmpty) : 0)}</b>
            <small>{t.collections.vsEarlier(money(prevSlots ? Math.trunc(prevTotal / prevSlots) : 0))}</small>
          </div>
          {total > 0 ? <p className={s.splitNote}>{t.collections.best(hourLabel(hoursAll[best]!), money(curV[best]!))}</p> : null}
        </div>
      </div>
    );
  };

  return (
    <Card aria-label={t.collections.title}>
      <CardHeader title={t.collections.title} action={<><div className={s.leg}><span><i />{range === "today" ? t.collections.legendToday : t.collections.legendYesterday}</span><span><i className={s.p} />{t.collections.legendPrev}</span></div><ViewAll as={Link} to="/billing" /></>} />
      {body()}
    </Card>
  );
}

/* ------------------------------------------------------------------ needs attention */

export function AttentionCard({ sc }: { sc: Scope }) {
  const dues = useDues(sc);
  const occ = useOccurrences(sc);
  const [open, setOpen] = useState(false);
  const { ctx } = sc;
  const currency = ctx.me?.business.currency ?? dues.data?.currency ?? "";
  const money = moneyIn(currency, ctx.locale);

  if (!dues.enabled && !ctx.has("classes.view")) return null;

  const pending = dues.isPending || (ctx.has("classes.view") && occ.isPending);
  const failure = dues.error ?? occ.error;
  const overdueBills = dues.data ? dues.data.dues.filter((b) => b.balance > 0 && b.dueDate !== null && b.dueDate < sc.date).sort((a, b) => (a.dueDate! < b.dueDate! ? -1 : 1)) : [];
  const nowMin = minutesOfDay(sc.nowMs, ctx.timeZone);
  const uncovered = (occ.data?.occurrences ?? []).filter((o) => o.trainerId === null && o.status === "scheduled" && hhmmToMinutes(o.end) > nowMin);

  const rows: ReactElement[] = [];
  if (overdueBills.length > 0) {
    const sm = summariseDues(overdueBills, sc.date);
    rows.push(<AttentionRow key="dues" icon="wallet" tone="crit" title={t.attention.dues} subtitle={t.attention.duesSub(sm.overdueCount)} figure={money(sm.overdueTotal)} action={<Button onClick={() => setOpen(true)} aria-haspopup="dialog">{t.attention.viewDues}</Button>} />);
  }
  for (const o of uncovered) {
    rows.push(<AttentionRow key={o.id} icon="badge" tone="warn" title={t.attention.cover} subtitle={t.attention.coverSub(o.name, o.start)} badge={{ label: t.attention.coverBadge, tone: "warn" }} action={<Button as={Link} to="/classes">{t.attention.findCover}</Button>} />);
  }

  const cols: Column<DueRow>[] = [
    { key: "m", header: t.attention.cols.member, cell: (b) => <span className={s.who}><Avatar id={b.memberId} name={b.buyerName ?? "?"} size="sm" />{b.buyerName ?? "Member"}</span> },
    { key: "d", header: t.attention.cols.due, cell: (b) => formatDate(b.dueDate!, ctx.locale, "weekday") },
    { key: "o", header: t.attention.cols.owed, align: "end", cell: (b) => money(b.balance) },
  ];

  return (
    <Card aria-label={t.attention.title}>
      <CardHeader title={t.attention.title} subtitle={!pending && !failure ? t.attention.sub(rows.length) : undefined} />
      {pending ? <CardSkeleton rows={2} /> : failure ? <FailureBody error={failure} title={t.attention.error} what="What needs attention" onRetry={() => { dues.refetch(); void occ.refetch(); }} /> : rows.length === 0 ? (
        <EmptyState compact icon="check" title={t.attention.empty} description={t.attention.emptyBody} />
      ) : <>{rows}</>}
      <Drawer open={open} onOpenChange={setOpen} title={t.attention.drawerTitle} description={t.attention.drawerSub} footer={<><Button onClick={() => setOpen(false)}>{t.appointments.drawer.close}</Button><Button variant="primary" as={Link} to="/dues" onClick={() => setOpen(false)}>{t.attention.openDues}</Button></>}>
        <Table label={t.attention.drawerTitle} columns={cols} rows={overdueBills} rowKey={(b) => b.billId} />
      </Drawer>
    </Card>
  );
}

/* ------------------------------------------------------------------ schedule */

export function ScheduleCard({ sc }: { sc: Scope }) {
  const q = useOccurrences(sc);
  const { ctx } = sc;
  if (!ctx.has("classes.view")) return null;
  const nowMin = minutesOfDay(sc.nowMs, ctx.timeZone);
  const list = [...(q.data?.occurrences ?? [])].filter((o) => o.status !== "cancelled").sort((a, b) => (a.start < b.start ? -1 : 1));
  const booked = sumMinor(list.map((o) => o.seatsTaken ?? 0));
  const cap = sumMinor(list.map((o) => o.capacity));
  const wait = sumMinor(list.map((o) => o.waitlisted ?? 0));
  const endMin = (o: Occurrence) => hhmmToMinutes(o.end);
  const next = list.find((o) => endMin(o) > nowMin);

  return (
    <Card aria-label={t.schedule.title}>
      <CardHeader title={t.schedule.title} subtitle={q.data ? t.schedule.sub(list.length) : undefined} action={<ViewAll as={Link} to="/classes" />} />
      {q.isPending ? <CardSkeleton /> : q.error ? <FailureBody error={q.error} title={t.schedule.error} what="Today's schedule" onRetry={() => void q.refetch()} /> : list.length === 0 ? (
        <EmptyState compact icon="dumbbell" title={t.schedule.empty} description={t.schedule.emptyBody} />
      ) : (
        <>
          <div className={s.fill}>
            {list.map((o) => {
              const past = endMin(o) <= nowMin;
              const taken = o.seatsTaken ?? 0;
              const full = taken >= o.capacity;
              return (
                <div key={o.id} className={`${s.sch} ${past ? s.pastCls : ""}`}>
                  <b>{o.start}</b>
                  <div>
                    <span style={{ fontWeight: 500 }}>{o.name}</span> {o === next && !past ? <Badge tone="info">{t.schedule.next}</Badge> : null}{o.trainerId === null ? <> <Badge tone="warn" dot>{t.schedule.noCoach}</Badge></> : null}
                    <small>{o.end ? `${o.start}–${o.end}` : ""}{past ? " · done" : ""}{o.waitlisted ? ` · ${o.waitlisted} waitlisted` : ""}</small>
                  </div>
                  <div className={`${s.occ} ${full ? s.fullOcc : ""}`}><span>{full ? t.schedule.full : t.schedule.of(taken, o.capacity)}</span><div className={s.bar}><i style={{ width: `${o.capacity ? Math.min(100, sharePct(taken, o.capacity)) : 0}%` }} /></div></div>
                </div>
              );
            })}
          </div>
          <CardFooter><span><b>{booked} of {cap}</b> class spots booked ({Math.round(sharePct(booked, cap))}%)</span><span>{t.schedule.waitlists(wait)}</span></CardFooter>
        </>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ renewals */

export function RenewalsCard({ sc }: { sc: Scope }) {
  const q = useFollowups(sc);
  const { ctx } = sc;
  const days = Array.from({ length: 7 }, (_, i) => addDays(sc.date, i));
  const renewals = (q.data?.items ?? []).filter((f) => f.kind === "renewal" && f.dueDate !== null);
  const per = days.map((d) => renewals.filter((f) => f.dueDate === d).length);
  const max = Math.max(...per, 1);
  const total = sumMinor(per);
  return (
    <Card aria-label={t.renewals.title}>
      <CardHeader title={t.renewals.title} subtitle={q.data ? t.renewals.sub(total) : undefined} />
      {q.isPending ? <CardSkeleton rows={2} /> : q.error ? <FailureBody error={q.error} title={t.renewals.error} what="Renewals" onRetry={() => void q.refetch()} /> : total === 0 ? (
        <EmptyState compact icon="clock" title={t.renewals.empty} description={t.renewals.emptyBody} />
      ) : (
        <div className={s.tl}>
          {days.map((d, i) => (
            <div key={d} className={`${s.tlCol} ${i === 0 ? s.todayCol : ""}`} role="img" aria-label={t.renewals.colLabel(i === 0 ? t.renewals.today : formatDate(d, ctx.locale, "weekday"), per[i]!)}>
              <div className={s.colBar}><i style={{ height: `${Math.max(4, (per[i]! / max) * 100)}%`, animationDelay: `${i * 40}ms` }} /></div>
              <b>{per[i]}</b>
              <small>{i === 0 ? t.renewals.today : formatDate(d, ctx.locale, "weekday").split(" ")[0]}</small>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ today's bills */

const BILL_TONE = { paid: "ok", overpaid: "ok", partial: "warn", unpaid: "info" } as const;
const PAGE_SIZE = 6;

export function BillsCard({ sc }: { sc: Scope }) {
  const d = useRangeData(sc, "today");
  const [shown, setShown] = useState(PAGE_SIZE);
  const { ctx } = sc;
  const q = d.billsCur;
  const allowed = ctx.has("billing.bills_view") && ctx.has("revenue.bill_amounts");
  const bills = useMemo(() => [...(q.data?.bills ?? [])].filter((b) => b.voidedAt === null).sort((a, b) => b.createdAt - a.createdAt), [q.data]);
  if (!allowed) return null;
  const currency = ctx.me?.business.currency ?? bills[0]?.currency ?? "";
  const money = moneyIn(currency, ctx.locale);
  // Null without revenue.bill_amounts: then the footer shows dashes, never a partial sum.
  const billed = sumKnown(bills.map((b) => b.total));
  const collected = sumKnown(bills.map((b) => (b.paidTotal === null || b.total === null ? null : Math.min(b.paidTotal, b.total))));
  const pending = sumKnown(bills.map((b) => b.balance)); // The API's balance: total less payments less credit notes.
  const cols: Column<BillSummary>[] = [
    { key: "id", header: t.bills.cols.bill, cell: (b) => <><b style={{ fontWeight: 500 }}>{b.id.slice(-6).toUpperCase()}</b><small>{formatTime(b.createdAt, ctx.timeZone)}</small></> },
    { key: "m", header: t.bills.cols.member, cell: (b) => <span className={s.who}><Avatar id={b.memberId} name={b.buyerName ?? "?"} size="sm" /><span title={b.buyerName ?? undefined}>{b.buyerName ?? "Member"}</span></span> },
    { key: "a", header: t.bills.cols.amount, align: "end", cell: (b) => money(b.total) },
    { key: "s", header: t.bills.cols.status, cell: (b) => <Badge tone={BILL_TONE[b.paymentStatus]} dot>{t.bills.status[b.paymentStatus]}</Badge> },
  ];
  return (
    <Card aria-label={t.bills.title}>
      <CardHeader title={t.bills.title} subtitle={t.bills.sub} action={<ViewAll as={Link} to="/billing" />} />
      {q.isPending ? <CardSkeleton /> : q.error ? <FailureBody error={q.error} title={t.bills.error} what="Today's bills" onRetry={() => void q.refetch()} /> : bills.length === 0 ? (
        <EmptyState compact icon="bill" title={t.bills.empty} description={t.bills.emptyBody} />
      ) : (
        <>
          <Table label={t.bills.title} columns={cols} rows={bills.slice(0, shown)} rowKey={(b) => b.id} minWidth={520}
            summary={t.bills.summary(Math.min(shown, bills.length), bills.length) + (q.data?.nextCursor ? "+" : "")} hasMore={shown < bills.length} onLoadMore={() => setShown((n) => n + PAGE_SIZE)} />
          <CardFooter><span>{t.bills.billed(bills.length, money(billed))}</span><span>{t.bills.collected(money(collected), money(pending))}</span></CardFooter>
        </>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ not connected */

export function NotConnectedCard({ title, what }: { title: string; what: string }) {
  return (
    <Card aria-label={title}>
      <CardHeader title={title} />
      <NotConnectedBody what={what} />
    </Card>
  );
}

